import React, { useState, useEffect } from 'react';
import { fetchWithAuth, isSuperAdmin, getRole, getCurrentUserId } from '../utils/auth';
import { API_URL } from '../config';
import AdminModal from './AdminModal';

// Roles branch_admin can assign when GET /api/admin/roles (super_admin-only)
// 403s -- loadRoles() falls back to this fixed set instead of leaving the
// dropdown (and thus form.role) empty. Temporary hardcoded list -- update
// alongside ASSIGNABLE_BY_BRANCH_ADMIN in backend/db/repositories/user_repository.py
// if the assignable set changes.
const BUILTIN_ASSIGNABLE = [
  { role: 'registrar', label: 'Registrar', is_builtin: false },
  { role: 'evaluator', label: 'Evaluator', is_builtin: false },
];

const AdminUserManager = () => {
  const [users, setUsers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [allRoles, setAllRoles] = useState([]);   // [{role, label, is_builtin}]
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ username: '', email: '', password: '', role: 'teacher', branch_id: '' });
  const [saving, setSaving] = useState(false);
  const [branchFilter, setBranchFilter] = useState('');

  const superAdmin = isSuperAdmin();
  const currentRole = getRole();

  useEffect(() => {
    loadUsers();
    loadRoles();
    if (superAdmin) loadBranches();
  }, []);

  const loadUsers = async () => {
    setLoading(true);
    setError('');
    try {
      const params = branchFilter ? `?branch_id=${branchFilter}` : '';
      const res = await fetchWithAuth(`${API_URL}/api/admin/users${params}`);
      const data = await res.json();
      if (res.ok) setUsers(data.users || []);
      else setError(data.error || 'Failed to load users');
    } catch {
      setError('Network error');
    } finally {
      setLoading(false);
    }
  };

  const loadBranches = async () => {
    try {
      const res = await fetchWithAuth(`${API_URL}/api/admin/branches`);
      const data = await res.json();
      if (res.ok) setBranches(data.branches || []);
    } catch {}
  };

  const loadRoles = async () => {
    try {
      const res = await fetchWithAuth(`${API_URL}/api/admin/roles`);
      const data = await res.json();
      if (res.ok) {
        setAllRoles(data.roles || []);
      } else if (!superAdmin) {
        setAllRoles(BUILTIN_ASSIGNABLE);
      }
    } catch {
      if (!superAdmin) setAllRoles(BUILTIN_ASSIGNABLE);
    }
  };

  useEffect(() => { loadUsers(); }, [branchFilter]);

  const handleOpenCreate = () => {
    setEditingId(null);
    // Default to whatever role is actually first in the dropdown -- a
    // hardcoded 'teacher' here would silently mismatch what's on screen
    // whenever 'teacher' isn't (or isn't first in) the assignable set,
    // since a <select> displays its first option even when its bound
    // value matches nothing, without that ever updating form.role.
    setForm({ username: '', email: '', password: '', role: assignableRoles[0]?.role || '', branch_id: '' });
    setError('');
    setShowForm(true);
  };

  const handleOpenEdit = (user) => {
    setEditingId(user.id);
    setForm({
      username: user.username,
      email: user.email || '',
      password: '',
      role: user.role,
      branch_id: user.branch_id || '',
    });
    setError('');
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setError('');
  };

  const handleSave = async () => {
    if (!editingId && !form.username.trim()) { setError('Username is required'); return; }
    if (!editingId && !form.password.trim()) { setError('Password is required for new users'); return; }
    if (!form.role) { setError('Role is required'); return; }

    setSaving(true);
    setError('');
    try {
      const payload = { ...form };
      if (!payload.password) delete payload.password;
      if (!payload.email) delete payload.email;
      if (payload.branch_id === '') payload.branch_id = null;

      const url = editingId
        ? `${API_URL}/api/admin/users/${editingId}`
        : `${API_URL}/api/admin/users`;
      const method = editingId ? 'PUT' : 'POST';
      const res = await fetchWithAuth(url, { method, body: JSON.stringify(payload) });
      const data = await res.json();
      if (res.ok && data.success) {
        setShowForm(false);
        loadUsers();
      } else {
        setError(data.error || 'Save failed');
      }
    } catch {
      setError('Network error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id, username) => {
    if (!window.confirm(`Delete user "${username}"?`)) return;
    try {
      const res = await fetchWithAuth(`${API_URL}/api/admin/users/${id}`, { method: 'DELETE' });
      const data = await res.json();
      if (res.ok && data.success) loadUsers();
      else setError(data.error || 'Delete failed');
    } catch {
      setError('Network error');
    }
  };

  // Alternative to delete for a user whose audit/registration history
  // blocks a hard delete -- deactivating rejects them at login without
  // touching any past record they're referenced from.
  const handleToggleActive = async (id, username, currentlyActive) => {
    const verb = currentlyActive ? 'Deactivate' : 'Reactivate';
    if (!window.confirm(`${verb} user "${username}"?`)) return;
    try {
      const res = await fetchWithAuth(`${API_URL}/api/admin/users/${id}/active`, {
        method: 'PUT', body: JSON.stringify({ is_active: !currentlyActive }),
      });
      const data = await res.json();
      if (res.ok && data.success) loadUsers();
      else setError(data.error || `${verb} failed`);
    } catch {
      setError('Network error');
    }
  };

  // Roles available in the dropdown, derived from DB
  const assignableRoles = superAdmin
    ? allRoles
    : allRoles.filter(r => !['super_admin', 'branch_admin'].includes(r.role));
  const selectedRoleMeta = allRoles.find(r => r.role === form.role);

  return (
    <div style={{ padding: '24px', maxWidth: '1000px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h2 style={{ margin: 0 }}>User Management</h2>
        <button className="btn-primary" onClick={handleOpenCreate}>+ New User</button>
      </div>

      {superAdmin && branches.length > 0 && (
        <div style={{ marginBottom: '16px' }}>
          <label style={{ marginRight: '8px' }}>Filter by branch:</label>
          <select value={branchFilter} onChange={e => setBranchFilter(e.target.value)}>
            <option value="">All Branches</option>
            {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        </div>
      )}

      {error && !showForm && <div className="error-banner">{error}</div>}

      {showForm && (
        <AdminModal
          title={editingId ? 'Edit User' : 'Create User'}
          onClose={closeForm} onSubmit={handleSave} saving={saving} error={error}
        >
          {!editingId && (
            <div className="form-row">
              <label>Username *</label>
              <input
                type="text"
                value={form.username}
                onChange={e => setForm({ ...form, username: e.target.value })}
                autoComplete="off"
                autoFocus
              />
            </div>
          )}

          <div className="form-row">
            <label>Email</label>
            <input
              type="email"
              value={form.email}
              onChange={e => setForm({ ...form, email: e.target.value })}
              autoFocus={!!editingId}
            />
          </div>

          <div className="form-row">
            <label>{editingId ? 'New Password (leave blank to keep current)' : 'Password *'}</label>
            <input
              type="password"
              value={form.password}
              onChange={e => setForm({ ...form, password: e.target.value })}
              autoComplete="new-password"
            />
          </div>

          <div className="form-row">
            <label>Role *</label>
            <select value={form.role} onChange={e => setForm({ ...form, role: e.target.value })}>
              {assignableRoles.map(r => (
                <option key={r.role} value={r.role}>{r.label}</option>
              ))}
            </select>
            {selectedRoleMeta && (
              <small style={{ display: 'block', marginTop: '4px', color: '#666' }}>
                {selectedRoleMeta.is_builtin ? 'Built-in role' : 'Custom role'}
              </small>
            )}
          </div>

          {superAdmin && form.role !== 'super_admin' && (
            <div className="form-row">
              <label>Branch *</label>
              <select value={form.branch_id} onChange={e => setForm({ ...form, branch_id: e.target.value })}>
                <option value="">— Select branch —</option>
                {branches.map(b => <option key={b.id} value={b.id}>{b.name} ({b.code})</option>)}
              </select>
            </div>
          )}
        </AdminModal>
      )}

      {loading ? (
        <p>Loading…</p>
      ) : (
        <table className="admin-table">
          <thead>
            <tr>
              <th>Username</th>
              <th>Email</th>
              <th>Role</th>
              <th>Branch</th>
              <th>Status</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.filter(u => u.id !== getCurrentUserId()).map(u => (
              <tr key={u.id} style={u.is_active === false ? { opacity: 0.55 } : undefined}>
                <td>{u.username}</td>
                <td>{u.email || '—'}</td>
                <td><span className={`role-badge role-${u.role}`}>{u.role.replace('_', ' ')}</span></td>
                <td>{u.branch_name || (u.role === 'super_admin' ? 'All' : '—')}</td>
                <td>{u.is_active === false ? 'Inactive' : 'Active'}</td>
                <td>{u.created_at ? new Date(u.created_at).toLocaleDateString() : '—'}</td>
                <td>
                  <button className="btn-sm" onClick={() => handleOpenEdit(u)}>Edit</button>
                  {' '}
                  <button className="btn-sm" onClick={() => handleToggleActive(u.id, u.username, u.is_active !== false)}>
                    {u.is_active === false ? 'Reactivate' : 'Deactivate'}
                  </button>
                  {' '}
                  <button className="btn-sm btn-danger" onClick={() => handleDelete(u.id, u.username)}>Delete</button>
                </td>
              </tr>
            ))}
            {users.filter(u => u.id !== getCurrentUserId()).length === 0 && (
              <tr><td colSpan={7} style={{ textAlign: 'center', color: '#888' }}>No users found</td></tr>
            )}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default AdminUserManager;
